/**
 * Version footer on login screen (same pattern as game-client)
 */
import { useTranslation } from 'react-i18next';
import { version } from '../../version';
import { Pill, type PillTone } from '../../components/pill';
import { isEnvironment, type Environment } from '../../config/environment';
import styles from './version-footer.module.css';

interface VersionFooterProps {
  className?: string;
}

/**
 * Each environment's tone: a Pill tone rather than a Tailwind colour class, as
 * the badge sits on a dark surface, where the light `bg-green-100` chips it
 * used to render read as a foreign element. Typed by the portal's environments,
 * so one it gains needs a tone or the build fails.
 */
const ENV_TONE: Record<Environment, PillTone> = {
  production: 'success',
  staging: 'warning',
  develop: 'info',
  local: 'brand',
};

export function VersionFooter({ className = '' }: VersionFooterProps) {
  const { t, i18n } = useTranslation();
  // A local build's version.ts says "development"; the portal calls it local.
  // A deployment the portal does not know reads in develop's tone.
  const displayEnv = version.environment === 'development' ? 'local' : version.environment;
  const envTone = isEnvironment(displayEnv) ? ENV_TONE[displayEnv] : ENV_TONE.develop;

  const formatDate = (dateString: string) => {
    if (dateString === 'Not deployed') return dateString;
    try {
      return new Date(dateString).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className={`${styles.versionBox} ${className}`}>
      <div className={styles.versionBoxInner}>
        <div className={styles.versionBoxRow}>
          <div className={styles.versionBoxLeft}>
            <span className={styles.versionLabel}>{t('version.version')}:</span>
            <span className={styles.versionValue}>{version.semantic_version}</span>
            <span className={styles.versionSep} />
            <span className={styles.versionLabel}>{t('version.environment')}:</span>
            <Pill tone={envTone}>{displayEnv}</Pill>
            {version.short_sha !== 'local' && (
              <>
                <span className={styles.versionSep} />
                <span className={styles.versionLabel}>{t('version.build')}:</span>
                <span className={styles.versionSha}>{version.short_sha}</span>
              </>
            )}
          </div>
          {version.deployed_at !== 'Not deployed' && (
            <span className={styles.versionDeployed}>
              {t('version.deployed')}: {formatDate(version.deployed_at)}
            </span>
          )}
          <span className={styles.versionCopyright}>
            © {new Date().getFullYear()} Ponder Inc.
          </span>
        </div>
      </div>
    </div>
  );
}
